import React, {ReactNode} from "react";
import Header from "./header";
import Container from "@material-ui/core/Container";
import {Paper} from "@material-ui/core";
import Footer from "./footer";
import Typography from "@material-ui/core/Typography";
import {createStyles, makeStyles} from "@material-ui/core/styles";
import {OpenGraph} from "next-seo/lib/types";

const useStyles = makeStyles((theme) => createStyles({
    featuredPost: {
        [theme.breakpoints.down('sm')]: {
            minHeight: 250,
        },
        position: 'relative',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'top center',
        minHeight: 390,
    },
    featuredPostContent: {
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2),
            paddingTop: theme.spacing(17),
        },
        position: 'relative',
        padding: theme.spacing(5),
        paddingTop: theme.spacing(17),
        paddingBottom: 0
    },
    imageContainer: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
        '& img': {
            objectFit: 'cover',
            height: '100%',
            width: '100%',
        }
    },
    image: {
        objectFit: 'cover',
        height: '100%',
        width: '100%',
    },
    overlay: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
        backgroundColor: 'rgba(0,0,0,.3)',
    },
    pageDescDesktop: {
        fontSize: '1.6rem',
        lineHeight: 1.2,
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        }
    },
    pageDescMobile: {
        fontSize: '1.2rem',
        lineHeight: 1.2,
        padding: theme.spacing(2),
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
        }
    },
    pageContainer: {
        [theme.breakpoints.down('sm')]: {
            padding: 0
        },
    }
}));

interface PageProps {
    image: {
        src: string
        srcSet: string
    }
    webp: {
        src: string
        srcSet: string
    }
    metaTitle: string
    title: string
    description?: string
    children: ReactNode
    hideFeaturedPostContent?: boolean
    hideOverlay?: boolean
    noindex?: boolean
    nofollow?: boolean
    openGraph?: OpenGraph
}

function Page({image, webp, metaTitle, title, description, children, hideFeaturedPostContent, hideOverlay, ...props}: PageProps) {
    const classes = useStyles();

    return (
        <React.Fragment>
            <Header title={metaTitle} description={description} {...props}/>
            <Container maxWidth="lg" classes={{root: classes.pageContainer}}>
                <Paper elevation={2} square={true}>
                    <div className={classes.featuredPost}>
                        <div className={classes.imageContainer}>
                            <picture>
                                <source srcSet={webp.srcSet} type='image/webp' />
                                <img alt={title ?? ''} src={image.src} srcSet={image.srcSet}/>
                            </picture>
                        </div>
                        <div className={classes.overlay} />
                        {hideFeaturedPostContent !== true && (
                            <div className={classes.featuredPostContent}>
                                <Typography variant="h1" color="primary" gutterBottom>
                                    {title}
                                </Typography>
                                {description && (
                                    <Typography color="primary" paragraph className={classes.pageDescDesktop}>
                                        {description}
                                    </Typography>
                                )}
                            </div>
                        )}
                    </div>
                    {description && hideFeaturedPostContent !== true && (
                        <Typography color="textSecondary" paragraph className={classes.pageDescMobile}>
                            {description}
                        </Typography>
                    )}
                    {children}
                </Paper>
                <Footer />
            </Container>
        </React.Fragment>
    );
}

export default Page;
